/**
 * The component library's plumbing (PLAN.md §0: cockatrice-ui's `src/ui`, ported from JSX to hyperscript). Solid's
 * compiled JSX clones templates written as HTML strings, which the CSP's Trusted Types refuse (decision 44), so every
 * component here builds its nodes with `el()` and calls other components the way compiled JSX does: through
 * `component()`, with every prop that can change passed as a getter. A plain function prop stays a function: an event
 * handler is never read as a value, as Solid's hyperscript would read any function of no arguments.
 *
 * The rules a port follows, so its output matches the handoff's markup node for node:
 * - a value that can change is passed to `el()` as a function (`class: () => cls()`), never read while building;
 * - `<Show>` is `when()` / `whenSome()`, `<For>` is `each()`, `<Dynamic>` is `dynamic()`, `<Portal>` is `portal()`;
 * - `{...rest}` keeps the JSX's order: spread first (`<b {...rest} class=…>`) is `el(tag, spread(rest, own))`, the
 *   element's own attributes winning; spread last is `el(tag, mergeProps(own, rest))`, the caller's winning.
 */
import { createMemo, mergeProps, onCleanup, untrack } from 'solid-js';
import type { Component } from 'solid-js';
import { insert } from 'solid-js/web';
import { el, view } from './dom.ts';
import type { Attributes, Child } from './dom.ts';

export { each, el, indexed, when, whenSome } from './dom.ts';
export type { Attributes, Child, On } from './dom.ts';

/** Calls a component as compiled JSX does: once, untracked, with `props` as given (getters stay getters). */
export function component<P extends object>(fn: (props: P) => Child, props: P): Child {
  return view(fn, props);
}

/** An element's attributes from the props a component passes on (`{...rest}` first, as JSX writes it) and its own, its own winning. */
export function spread(rest: object, own: Attributes): Attributes {
  return mergeProps(rest, own);
}

/** Lazy `<Dynamic component={tag()}>`: the element made again, with its children, each time `tag` changes. */
export function dynamic(tag: () => string, attributes: Attributes, ...children: readonly Child[]): Child {
  const made = createMemo(() => {
    const name = tag();
    return untrack(() => el(name, attributes, ...children));
  });
  return () => made();
}

/**
 * `<Portal>`: `child` kept up to date in a `div` of its own at the end of `mount` (the page's body), and taken out
 * with the component that made it. As Solid's own Portal, it belongs to the component that calls it.
 */
export function portal(child: () => Child, mount: () => Node = () => document.body): Child {
  const host = document.createElement('div');
  insert(host, child);
  const target = mount();
  target.appendChild(host);
  onCleanup(() => {
    host.remove();
  });
  return null;
}

/** A component with no props of its own, typed for `component()`. */
export type NoProps = Record<string, never>;

/** A component's type: what `component()` takes. */
export type UiComponent<P extends object> = Component<P>;
